import {
  ActionSubmit,
  Form,
  LargeInput,
  ValidationContainer,
  ValidationMessage,
} from "@tandemdiabetes/sugarmate-react-components"
import { Link } from "@tanstack/react-router"
import type {
  FormEventHandler,
  ChangeEventHandler,
} from "react"
import {
  useState,
  useCallback,
} from "react"
import styles from "./SignIn.module.scss"
import {
  PublicPageHeader,
  LargePasswordInput,
} from "src/components"
import { usePolyglot } from "src/contexts"
import type { RoutePath } from "src/types"

const emailId = "user-email-input"
const passwordId = "user-password-input"
const forgotPasswordId = "forgotPasswordButton"
const polyglotPrefix = "pages.sign_in."

const emailErrorId = "user-email-input-error"
const passwordErrorId = "user-password-input-error"

interface Props {
  signIn?: (email: string, password: string) => Promise<void>;
  goBack: () => void;
  forgotPasswordRoute: RoutePath;
}

// TODO show "password criteria not met" error
export function SignInPage(props: Props) {
  const {
    signIn,
    goBack,
    forgotPasswordRoute,
  } = props

  const polyglot = usePolyglot()

  const [
    formState,
    setFormState,
  ] = useState<{
    email: string;
    password: string;
  }>({
    email: "",
    password: "",
  })

  const [
    submitButtonDisabled,
    setSubmitButtonDisabled,
  ] = useState(false)

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        setSubmitButtonDisabled(true)

        try {
          if (!signIn) {
            throw new Error("cannot sign in")
          }

          await signIn(
            formState.email,
            formState.password,
          )
        } catch {
          // do nothing
        }

        setSubmitButtonDisabled(false)
      })()
    },
    [
      formState.email,
      formState.password,
      signIn,
    ],
  )

  const onEmailChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setFormState({
        ...formState,
        email: e.target.value,
      })
    },
    [
      setFormState,
      formState,
    ],
  )

  const onPasswordChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setFormState({
        ...formState,
        password: e.target.value,
      })
    },
    [
      setFormState,
      formState,
    ],
  )

  return (
    <div className={styles.container}>
      <PublicPageHeader onBackClick={goBack}>
        {polyglot.t(`${polyglotPrefix}title`)}
      </PublicPageHeader>
      <Form
        className={styles.form}
        onSubmit={onSubmit}
      >
        <ValidationContainer>
          <LargeInput
            id={emailId}
            placeholder={polyglot.t(`${polyglotPrefix}email.placeholder`)}
            type="email"
            className={styles.input}
            value={formState.email}
            onChange={onEmailChange}
            autoComplete="email"
            required
          />
          <ValidationMessage
            id={emailErrorId}
            invalidMessage={{
              valueMissing: polyglot.t("validation_message.value_missing"),
              typeMismatch: polyglot.t(
                "validation_message.email_type_mismatch",
              ),
            }}
          />
        </ValidationContainer>
        <ValidationContainer>
          <LargePasswordInput
            id={passwordId}
            placeholder={polyglot.t(`${polyglotPrefix}password.placeholder`)}
            className={styles.input}
            value={formState.password}
            onChange={onPasswordChange}
            autoComplete="current-password"
            required
          />
          <ValidationMessage
            id={passwordErrorId}
            invalidMessage={{
              valueMissing: polyglot.t("validation_message.value_missing"),
            }}
          />
        </ValidationContainer>
        <Link
          to={forgotPasswordRoute}
          className={styles.link}
          id={forgotPasswordId}
          params={{}}
          search={{}}
        >
          {polyglot.t(`${polyglotPrefix}forgot_password_link`)}
        </Link>
        <ActionSubmit
          disabled={submitButtonDisabled || !signIn}
          value={polyglot.t(`${polyglotPrefix}submit_cta`)}
          stylePreset="primary"
          className={styles.submit}
        />
      </Form>
    </div>
  )
}
